import React, { useContext, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { vehicleContext } from '../VehicleProvider';
import Collapse from '../Collapse';
import { PriceBreakdownType, selectPriceBreakdowns } from '../../redux/priceBreakdownReducer';
import PriceBreakdownInfoBox from '../PriceBreakdownInfoBox';
import PriceBreakdownInfoBoxT3 from '../PriceBreakdownInfoBox-T3';
import { Wrapper, SectionTitle, Container } from './ContentWrapper.styles';
import { IS_T3_WEBSITE } from '../../constants';
import { parseConfigs } from '../../ContentMapping/cleanConfigs';

const ContentWrapper: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isTablet = useMediaQuery({ width: 1024 });
  const { loading, vehicle, market } = useContext(vehicleContext);
  const isCanadian = market === 'ca' || market === 'cauc';
  const hideSection = isCanadian && !vehicle?.isAvailableNow;
  const priceBreakdownState = useSelector(selectPriceBreakdowns)
    ?.priceBreakdownGroup as PriceBreakdownType;
  const isNewVehicle = vehicle?.vehicleType === 'new';
  const cleanedPriceConfigs = useMemo(() => {
    return parseConfigs(priceBreakdownState, isNewVehicle);
  }, [isNewVehicle, priceBreakdownState]);
  const displayRebates = useSelector(selectPriceBreakdowns)?.displayRebates || false;
  const displayRebatesUsed = useSelector(selectPriceBreakdowns)?.displayRebatesUsed || false;

  const renderTier3Content = useMemo(() => {
    // if it's T3 but no price is enabled or there is no config we should not render the component at all
    if (!priceBreakdownState) return null;
    if (!Object.keys(priceBreakdownState).length) return null;
    if (loading) {
      return !isTablet ? <Skeleton /> : <Skeleton height={1018} />;
    }
    return (
      <PriceBreakdownInfoBoxT3
        breakdownConfig={cleanedPriceConfigs}
        displayRebates={displayRebates}
        displayRebatesUsed={displayRebatesUsed}
      />
    );
  }, [
    cleanedPriceConfigs,
    displayRebates,
    displayRebatesUsed,
    isTablet,
    loading,
    priceBreakdownState,
  ]);

  const renderContent = useMemo(() => {
    if (loading) {
      return !isTablet ? <Skeleton /> : <Skeleton height={1018} />;
    }
    return (
      <Wrapper>
        {!isMobile ? (
          <>
            <SectionTitle>{t('pricingBreakdown.priceDetails.heading')}</SectionTitle>
            <Container>
              <PriceBreakdownInfoBox />
            </Container>
          </>
        ) : (
          <>
            <Collapse heading={t('pricingBreakdown.priceDetails.heading')}>
              <Container>
                <PriceBreakdownInfoBox />
              </Container>
            </Collapse>
          </>
        )}
      </Wrapper>
    );
  }, [isMobile, isTablet, loading, t]);

  return vehicle || loading ? <>{IS_T3_WEBSITE ? renderTier3Content : renderContent}</> : null;
};

export default ContentWrapper;
